import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLossReasonInput,
  CreatePipelineInput,
  CreateStageInput,
  LossReasonResponse,
  PipelineResponse,
  UpdateLossReasonInput,
  UpdatePipelineInput,
  UpdateStageInput,
} from '@orion/contracts';
import { api } from '../../../lib/api-client.js';

const KEY = ['admin-pipelines'];

export function useAdminPipelinesQuery() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<PipelineResponse[]>('/admin/pipelines') });
}

export function useCreatePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePipelineInput) => api.post<PipelineResponse>('/admin/pipelines', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePipelineInput }) => api.patch<PipelineResponse>(`/admin/pipelines/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/pipelines/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, input }: { pipelineId: string; input: CreateStageInput }) =>
      api.post<PipelineResponse>(`/admin/pipelines/${pipelineId}/stages`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageId, input }: { pipelineId: string; stageId: string; input: UpdateStageInput }) =>
      api.patch<PipelineResponse>(`/admin/pipelines/${pipelineId}/stages/${stageId}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      api.delete<PipelineResponse>(`/admin/pipelines/${pipelineId}/stages/${stageId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReorderStages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, orderedIds }: { pipelineId: string; orderedIds: string[] }) =>
      api.patch<PipelineResponse>(`/admin/pipelines/${pipelineId}/stages/reorder`, { orderedIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

const LOSS_REASONS_KEY = ['admin-loss-reasons'];

export function useLossReasonsQuery() {
  return useQuery({ queryKey: LOSS_REASONS_KEY, queryFn: () => api.get<LossReasonResponse[]>('/admin/pipelines/loss-reasons') });
}

export function useCreateLossReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLossReasonInput) => api.post<LossReasonResponse>('/admin/pipelines/loss-reasons', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LOSS_REASONS_KEY }),
  });
}

export function useUpdateLossReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLossReasonInput }) => api.patch<LossReasonResponse>(`/admin/pipelines/loss-reasons/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LOSS_REASONS_KEY }),
  });
}

export function useDeleteLossReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/pipelines/loss-reasons/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LOSS_REASONS_KEY }),
  });
}
